import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar as CalendarIcon, Clock, User, ArrowLeft, Phone, Edit, Trash2, Send, Plus, MoreVertical, CheckCircle, XCircle, AlertCircle, UserPlus, ChevronDown, ChevronUp, Heart, Tag as TagIcon, FileText } from 'lucide-react';
import { appointmentsAPI, profileAPI, servicesAPI, branchesAPI } from '../services/api';
import { format, isSameDay, parseISO, startOfDay, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from '../hooks/use-toast';
import TagSelector from '../components/TagSelector';
import RecommendationsManager from '../components/RecommendationsManager';
import WhatsAppMessageEditor from '../components/WhatsAppMessageEditor';
import { STORAGE_KEYS, DEFAULT_TAGS } from '../mock';

const CalendarView = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [selectedAppointmentForTags, setSelectedAppointmentForTags] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [selectedAppointmentForNotes, setSelectedAppointmentForNotes] = useState(null);
  const [showWhatsAppEditor, setShowWhatsAppEditor] = useState(false);
  const [selectedAppointmentForWA, setSelectedAppointmentForWA] = useState(null);

  useEffect(() => {
    // Don't redirect while still loading auth state
    if (authLoading) {
      return;
    }
    
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    loadAppointments();
    loadServices();
    loadBranches();
    loadTags();
  }, [currentUser, authLoading, navigate]);

  const loadTags = async () => {
    try {
      const response = await profileAPI.get();
      const profileData = response.data;
      const loadedTags = profileData.patientTags || DEFAULT_TAGS;
      setAvailableTags(loadedTags);
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags(DEFAULT_TAGS);
    }
  };

  useEffect(() => {
    filterAppointmentsByDate(selectedDate);
  }, [selectedDate, appointments]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const response = await appointmentsAPI.getAll();
      setAppointments(response.data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las citas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const filterAppointmentsByDate = (date) => {
    if (!date) {
      setSelectedDayAppointments([]);
      return;
    }
    
    // Format the selected date as YYYY-MM-DD for comparison
    const selectedDateString = format(date, 'yyyy-MM-dd');
    
    const filtered = appointments.filter(apt => {
      // Ensure apt.date is in YYYY-MM-DD format for comparison
      const aptDateString = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      return aptDateString === selectedDateString;
    });
    
    setSelectedDayAppointments(filtered.sort((a, b) => a.time.localeCompare(b.time)));
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getDatesWithAppointments = () => {
    const uniqueDates = new Set();
    
    appointments.forEach(apt => {
      const dateStr = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      uniqueDates.add(dateStr);
    });
    
    return Array.from(uniqueDates).map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      // Create date in local timezone at noon to avoid any timezone conversion issues
      return new Date(year, month - 1, day, 12, 0, 0);
    });
  };

  const handleEditAppointment = (appointment) => {
    navigate('/dashboard', { state: { editAppointment: appointment } });
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cita?')) {
      return;
    }
    
    try {
      await appointmentsAPI.delete(id);
      toast({
        title: 'Cita eliminada',
        description: 'La cita se eliminó correctamente',
      });
      await loadAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la cita',
        variant: 'destructive',
      });
    }
  };

  const sendWhatsAppReminder = (appointment) => {
    setSelectedAppointmentForWA(appointment);
    setShowWhatsAppEditor(true);
  };

  const sendConfirmation = async (appointment) => {
    try {
      console.log('[CalendarView sendConfirmation] Starting with appointment:', appointment);
      
      // Load profile settings from API
      let profile = {};
      try {
        const response = await profileAPI.get();
        profile = response.data;
        console.log('[CalendarView sendConfirmation] Profile loaded:', profile);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      
      // Get confirmation template
      let template = profile.confirmationTemplate || '¡Hola {nombre}! 👋\n\nTu cita ha sido agendada exitosamente:\n\n📅 Fecha: {fecha}\n🕐 Hora: {hora}\n📋 Servicio: {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\n{recordatorio_nota}\n\n¡Te esperamos!';
      
      // Format date
      const appointmentDate = new Date(appointment.date + 'T00:00:00');
      const dayName = format(appointmentDate, 'EEEE', { locale: es }).toUpperCase();
      const dateFormatted = `${dayName} ${format(appointmentDate, 'd')}`;
      
      // Replace basic variables
      const firstName = appointment.patientName.split(' ')[0];
      template = template.replace('{nombre}', firstName);
      template = template.replace('{fecha}', dateFormatted);
      template = template.replace('{hora}', formatTimeTo12Hour(appointment.time));
      
      // Service details
      let serviceText = '';
      let serviceDuration = '';
      let serviceDescription = '';
      
      if (profile.confirmationIncludeService !== false) {
        const appointmentServiceName = appointment.serviceType || 'Consulta';
        const serviceDetails = services.find(s => s.name === appointmentServiceName);
        
        if (serviceDetails) {
          serviceText = serviceDetails.name;
          serviceDuration = serviceDetails.duration ? `⏱️ Duración: ${serviceDetails.duration}` : '';
          serviceDescription = serviceDetails.description || '';
        } else {
          serviceText = appointmentServiceName;
        }
      }
      
      template = template.replace('{servicio}', serviceText);
      template = template.replace('{servicio_duracion}', serviceDuration);
      template = template.replace('{servicio_descripcion}', serviceDescription);
      
      // Business name and specialty
      let businessSpecialtyText = '';
      if (profile.confirmationIncludeBusiness !== false && profile.businessName) {
        businessSpecialtyText += `📍 ${profile.businessName}`;
      }
      if (profile.confirmationIncludeSpecialty !== false && profile.speciality) {
        if (businessSpecialtyText) {
          businessSpecialtyText += ` - ${profile.speciality}`;
        } else {
          businessSpecialtyText = `👨‍⚕️ ${profile.speciality}`;
        }
      }
      template = template.replace('{negocio_especialidad}', businessSpecialtyText);
      
      // Address
      let addressText = '';
      if (profile.confirmationIncludeAddress !== false && appointment.branchId) {
        const branch = branches.find(b => b.id === appointment.branchId);
        if (branch) {
          addressText = `📍 Ubicación: ${branch.name}`;
          if (branch.address) {
            addressText += `\n${branch.address}`;
          }
          if (branch.gpsLocation) {
            addressText += `\n🗺️ Ver en mapa: https://www.google.com/maps?q=${encodeURIComponent(branch.gpsLocation)}`;
          }
        }
      }
      template = template.replace('{direccion}', addressText);
      
      // Reminder note
      let reminderNoteText = '';
      if (profile.confirmationIncludeReminderNote !== false) {
        reminderNoteText = '📬 Recibirás un recordatorio antes de tu cita.';
      }
      template = template.replace('{recordatorio_nota}', reminderNoteText);
      
      // Clean up multiple empty lines
      template = template.replace(/\n\n\n+/g, '\n\n').trim();
      
      console.log('[CalendarView sendConfirmation] Final message:', template);
      
      // Send WhatsApp
      const phoneNumber = appointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(template)}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: 'Confirmación enviada',
        description: 'El mensaje de confirmación se abrió en WhatsApp Business',
      });
    } catch (error) {
      console.error('Error sending confirmation:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar el mensaje de confirmación',
        variant: 'destructive',
      });
    }
  };

  const sendThanksMessage = async (appointment) => {
    try {
      // Load profile to get thanks template
      const response = await profileAPI.get();
      const profile = response.data;
      
      let message = profile.thanksTemplate || '¡Gracias por tu visita, {nombre}! 🙏\n\nEsperamos que hayas tenido una excelente experiencia.\n\n¡Esperamos verte pronto!';
      
      // Replace patient name (only first name)
      const firstName = appointment.patientName.split(' ')[0];
      message = message.replace('{nombre}', firstName);
      
      // Social links
      let socialText = '';
      if (profile.thanksIncludeSocial && profile.socialLinks) {
        const links = [];
        if (profile.socialLinks.facebook) links.push(`📘 Facebook: ${profile.socialLinks.facebook}`);
        if (profile.socialLinks.instagram) links.push(`📸 Instagram: ${profile.socialLinks.instagram}`);
        if (profile.socialLinks.whatsapp) links.push(`💬 WhatsApp: ${profile.socialLinks.whatsapp}`);
        if (links.length > 0) {
          socialText = '🌐 Síguenos en redes sociales:\n' + links.join('\n');
        }
      }
      message = message.replace('{redes_sociales}', socialText);
      
      // Review link
      let reviewText = '';
      if (profile.thanksIncludeReview && profile.reviewLink) {
        reviewText = `⭐ Ayúdanos con tu opinión:\n${profile.reviewLink}`;
      }
      message = message.replace('{link_calificacion}', reviewText);
      
      // Clean up multiple empty lines
      message = message.replace(/\n\n\n+/g, '\n\n').trim();
      
      // Open WhatsApp
      const phoneNumber = appointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: 'Mensaje de agradecimiento',
        description: 'El mensaje se abrió en WhatsApp Business',
      });
    } catch (error) {
      console.error('Error sending thanks message:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la plantilla de agradecimiento',
        variant: 'destructive',
      });
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      console.log('[CalendarView] Updating appointment status:', { id, status });
      await appointmentsAPI.updateStatus(id, status);
      console.log('[CalendarView] Status updated successfully');
      toast({
        title: 'Estado actualizado',
        description: 'El estado de la cita se actualizó correctamente',
      });
      await loadAppointments();
    } catch (error) {
      console.error('Error updating appointment status:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado de la cita',
        variant: 'destructive',
      });
    }
  };

  const handleSaveTags = async (appointmentId, tagIds) => {
    try {
      // Get the current appointment
      const appointment = appointments.find(apt => apt._id === appointmentId || apt.id === appointmentId);
      if (!appointment) return;
      
      // Update appointment with new tags
      await appointmentsAPI.update(appointmentId, { ...appointment, tags: tagIds });
      
      // Reload appointments
      await loadAppointments();
      
      toast({
        title: 'Etiquetas actualizadas',
        description: 'Las etiquetas se asignaron correctamente',
      });
    } catch (error) {
      console.error('Error saving tags:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar las etiquetas',
        variant: 'destructive',
      });
    }
  };

  const saveContactToPhone = (appointment) => {
    try {
      // Create vCard format
      const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${appointment.patientName}
TEL;TYPE=CELL:${appointment.phone}
END:VCARD`;

      // Create blob and download
      const blob = new Blob([vCard], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${appointment.patientName.replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Contacto descargado',
        description: 'Abre el archivo para guardar el contacto en tu teléfono',
      });
    } catch (error) {
      console.error('Error saving contact:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el contacto',
        variant: 'destructive',
      });
    }
  };

  const toggleCardExpansion = (appointmentId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
    } catch {
      return dateString;
    }
  };

  const handleCreateAppointmentForDate = () => {
    // Navigate to dashboard with the selected date
    navigate('/dashboard', { 
      state: { 
        createAppointment: true,
        preselectedDate: format(selectedDate, 'yyyy-MM-dd')
      } 
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: { text: 'Confirmada', className: 'bg-green-100 text-green-800 border-green-300' },
      pending: { text: 'Pendiente', className: 'bg-blue-100 text-blue-800 border-blue-300' },
      cancelled: { text: 'Cancelada', className: 'bg-red-100 text-red-800 border-red-300' },
      cancelled_patient: { text: 'Cancelada', className: 'bg-red-100 text-red-800 border-red-300' },
      completed: { text: 'Completada', className: 'bg-purple-100 text-purple-800 border-purple-300' },
      no_show: { text: 'No asistió', className: 'bg-gray-100 text-gray-800 border-gray-300' }
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.text}</Badge>;
  };

  const datesWithAppointments = getDatesWithAppointments();
  console.log('[CalendarView] Dates with appointments:', datesWithAppointments);
  console.log('[CalendarView] Total appointments:', appointments.length);
  
  const modifiers = {
    hasAppointment: datesWithAppointments
  };

  const modifiersStyles = {
    hasAppointment: {
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
      color: 'white',
      borderRadius: '0.5rem',
      border: '2px solid #059669',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
    }
  };

  const modifiersClassNames = {
    hasAppointment: 'has-appointment-day'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-4 hover:bg-blue-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-2xl">
                <CalendarIcon className="w-6 h-6 mr-2" />
                Calendario de Citas
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={es}
                className="rounded-md border shadow-sm"
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                modifiersClassNames={modifiersClassNames}
              />
            </CardContent>
          </Card>

          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-t-lg">
              <div className="flex justify-between items-center">
                <CardTitle className="text-xl">
                  Citas del {format(selectedDate, 'PPP', { locale: es })}
                </CardTitle>
                <Button
                  onClick={handleCreateAppointmentForDate}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nueva Cita
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {selectedDayAppointments.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">No hay citas programadas para este día</p>
                  <Button
                    onClick={handleCreateAppointmentForDate}
                    className="bg-gradient-to-r from-blue-500 to-green-500 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Cita para este Día
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 min-h-[200px]">
                  {selectedDayAppointments.map((appointment) => {
                    const aptId = appointment._id || appointment.id;
                    const isExpanded = expandedCards.has(aptId);
                    
                    return (
                    <Card key={aptId} className="border-l-4 border-l-blue-500 hover:shadow-md transition-all duration-300">
                      <CardContent className="py-2 px-3">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-2 text-blue-500" />
                                <h3 className="font-semibold text-base">{appointment.patientName}</h3>
                              </div>
                              <div className="flex items-center gap-1">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="h-8 w-8 flex items-center justify-center rounded-md transition-colors cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem onClick={() => sendConfirmation(appointment)}>
                                      <Phone className="w-4 h-4 mr-2 text-blue-600" />
                                      Enviar confirmación
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => saveContactToPhone(appointment)}>
                                      <UserPlus className="w-4 h-4 mr-2 text-green-600" />
                                      Guardar contacto
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => sendThanksMessage(appointment)}>
                                      <Heart className="w-4 h-4 mr-2 text-purple-600" />
                                      Enviar agradecimiento
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedAppointmentForTags(appointment);
                                      setShowTagSelector(true);
                                    }}>
                                      <TagIcon className="w-4 h-4 mr-2 text-blue-600" />
                                      Asignar etiquetas
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedAppointmentForNotes(appointment);
                                      setShowRecommendations(true);
                                    }}>
                                      <FileText className="w-4 h-4 mr-2 text-indigo-600" />
                                      Notas
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => updateAppointmentStatus(aptId, 'confirmed')}>
                                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                      Marcar confirmada
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateAppointmentStatus(aptId, 'completed')}>
                                      <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                                      Marcar completada
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateAppointmentStatus(aptId, 'cancelled')}>
                                      <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                      Cancelar cita
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateAppointmentStatus(aptId, 'no_show')}>
                                      <AlertCircle className="w-4 h-4 mr-2 text-gray-600" />
                                      No asistió
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteAppointment(aptId)} className="text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Eliminar
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleCardExpansion(aptId)}
                                  className="h-6 w-6 p-0"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 items-center ml-6 mt-1 text-xs">
                              {appointment.status === 'confirmed' && (
                                <Badge className="bg-green-500 text-white text-xs py-0 px-2">Confirmada</Badge>
                              )}
                              {appointment.status === 'pending' && (
                                <Badge className="bg-yellow-500 text-white text-xs py-0 px-2">Pendiente</Badge>
                              )}
                              {appointment.status === 'cancelled' && (
                                <Badge className="bg-red-500 text-white text-xs py-0 px-2">Cancelada</Badge>
                              )}
                              {appointment.status === 'completed' && (
                                <Badge className="bg-purple-500 text-white text-xs py-0 px-2">Completada</Badge>
                              )}
                              {appointment.status === 'no_show' && (
                                <Badge className="bg-gray-500 text-white text-xs py-0 px-2">No asistió</Badge>
                              )}
                              {appointment.serviceType && (
                                <Badge className="bg-blue-600 text-white text-xs py-0 px-2">
                                  {appointment.serviceType}
                                </Badge>
                              )}
                              {appointment.tags && appointment.tags.length > 0 && (
                                appointment.tags.map(tagId => {
                                  const tag = availableTags.find(t => t.id === tagId);
                                  if (!tag) return null;
                                  return (
                                    <Badge 
                                      key={tagId}
                                      className="text-white text-xs py-0 px-2"
                                      style={{ backgroundColor: tag.color }}
                                    >
                                      {tag.name}
                                    </Badge>
                                  );
                                })
                              )}
                              <div className="flex items-center text-xs text-gray-600">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatTimeTo12Hour(appointment.time)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {isExpanded && (
                        <>
                        <div className="space-y-1.5 mt-2">
                          <div className="flex items-center text-sm text-gray-600 ml-6">
                            <Phone className="w-3 h-3 mr-2" />
                            {appointment.phone}
                          </div>
                          <div className="flex items-center text-sm text-gray-600 ml-6">
                            <CalendarIcon className="w-3 h-3 mr-2" />
                            {formatDate(appointment.date)}
                          </div>
                          <div className="pt-1.5 border-t mt-1.5 ml-6">
                            <p className="text-xs text-gray-500 mb-0.5">Motivo:</p>
                            <p className="text-sm text-gray-900">{appointment.reason}</p>
                          </div>
                          
                          <div className="space-y-1.5 pt-2 border-t mt-2">
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => sendWhatsAppReminder(appointment)}
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                                size="sm"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                WhatsApp
                              </Button>
                              <Button
                                onClick={() => handleEditAppointment(appointment)}
                                variant="outline"
                                size="sm"
                                className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                            </div>
                          </div>
                        </div>
                        </>
                        )}
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {showTagSelector && selectedAppointmentForTags && (
        <TagSelector
          appointment={selectedAppointmentForTags}
          onClose={() => {
            setShowTagSelector(false);
            setSelectedAppointmentForTags(null);
          }}
          onSave={(tagIds) => handleSaveTags(selectedAppointmentForTags._id || selectedAppointmentForTags.id, tagIds)}
        />
      )}

      {showRecommendations && selectedAppointmentForNotes && (
        <RecommendationsManager
          appointment={selectedAppointmentForNotes}
          onClose={() => {
            setShowRecommendations(false);
            setSelectedAppointmentForNotes(null);
          }}
        />
      )}

      {showWhatsAppEditor && selectedAppointmentForWA && (
        <WhatsAppMessageEditor
          appointment={selectedAppointmentForWA}
          onClose={() => {
            setShowWhatsAppEditor(false);
            setSelectedAppointmentForWA(null);
          }}
        />
      )}
    </div>
  );
};

export default CalendarView;